<template>
  <Modal v-if="grimoire.modal === 'reminder' && availableReminders.length && players[playerIndex]"
    @close="grimoire.toggleModal(null)">
    <h3>{{ t('modal.reminder.title') }}</h3>
    <ul class="reminders">
      <li v-for="reminder in availableReminders" :key="reminder.role?.id + ' ' + reminder.name" class="reminder"
        :class="[reminder.role?.id] + (reminder.special ? ' special' : '')" @click="addReminder(reminder)">
        <picture :class="reminder.role?.team">
          <RoleIcon :role="reminder.role" />
        </picture>
        <span class="text">{{ reminder.name }}</span>
      </li>
    </ul>
  </Modal>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { Modal, RoleIcon } from '@/components';
import { getRoleImage } from '@/services';
import {
  SPECIAL_REMINDER_ROLES,
  useGrimoireStore,
  useLocaleStore,
  usePlayersStore,
  useSessionStore,
  useDialogStore,
} from "@/stores";
import type {
  Player,
  Reminder,
  Role,
  SpecialFeature,
} from "@/types";

const locale = useLocaleStore();
const t = locale.t;
const session = useSessionStore();
const dialogStore = useDialogStore();

const mapReminder =
  (role: Role) =>
    (name: string): Reminder => ({
      role: role,
      image: getRoleImage(role),
      imageAlt: role?.imageAlt || '',
      name,
      id: `${role?.id}-${name}`,
    });

const props = defineProps<{
  playerIndex: number;
}>();

const playersStore = usePlayersStore();
const grimoire = useGrimoireStore();

const players = computed(() => playersStore.players);


function needsAReminder(feature: SpecialFeature | undefined) {
  if (feature === undefined) return false;

  // Will need to be edited when other features will be added
  return feature.name === "two-hands" || feature.name === "multiplier" || feature.name === "replace-character";
}

function featureMatchesTheGlobal(feature: SpecialFeature | undefined, global: boolean) {
  if (feature === undefined) {
    return false;
  }
  // Some features only make sense for global reminders
  if(feature.name === "replace-character") {
    return global;
  }
  // For other features, if feature.global is undefined, we treat this as if it was FALSE
  if (feature.global === undefined) {
    return !global;
  }
  return feature.global === global;
}

function addSpecialReminders(reminders: Reminder[], role: Role, global: boolean) {
  if (role?.special) {
    let remindersFromRole = global ? role?.remindersGlobal : role?.reminders;
    if (Array.isArray(role?.special)) {
      let cptRemind = 0;
      for (
        let cptSpecial = 0;
        cptSpecial < role?.special.length && cptRemind < (remindersFromRole?.length ?? 0);
        cptSpecial++
      ) {
        if (needsAReminder(role?.special[cptSpecial]) && featureMatchesTheGlobal(role?.special[cptSpecial], global)) {
          reminders.push({
            id: `${role?.id}-special-${cptSpecial}`,
            role: role,
            name: remindersFromRole![cptRemind]!,
            special: role?.special[cptSpecial]
          });
          cptRemind++;
        }
      }
    }
    // The "else" means if role?.special is a SpecialFeature, rather than SpecialFeature[]
    else if (
      needsAReminder(role.special) && featureMatchesTheGlobal(role?.special, global) && remindersFromRole && remindersFromRole?.length > 0
    ) {
      reminders.push({
        id: `${role?.id}-special`,
        role: role,
        name: remindersFromRole[0] ?? "",
        special: role?.special,
      });
    }
  }
}

const availableReminders = computed((): Reminder[] => {
  let reminders: Reminder[] = [];
  const { players, bluffs } = playersStore;

  grimoire.roles.forEach((role: Role) => {
    if (players.some((p: Player) => p.role?.id === role?.id) || bluffs.some((bluff: Role) => bluff.id === role?.id)) {
      if (role?.reminders) {
        reminders = [...reminders, ...role.reminders.map(mapReminder(role))];

        if (!session.isPlayerOrSpectator) {
          addSpecialReminders(reminders, role, false);
        }
      }
    }
    // add global reminders
    if (role?.remindersGlobal?.length) {
      reminders = [
        ...reminders,
        ...role.remindersGlobal.map(mapReminder(role)),
      ];

      if (!session.isPlayerOrSpectator) {
        addSpecialReminders(reminders, role, true);
      }
    }
  });
  // add fabled reminders
  playersStore.fabled.forEach((role: Role) => {
    if (role?.reminders) {
      reminders = [...reminders, ...role.reminders.map(mapReminder(role))];

      if (!session.isPlayerOrSpectator) {
        addSpecialReminders(reminders, role, false);
      }
    }
  });

  // add out of script traveler reminders
  grimoire.othertravelers.forEach((role: Role) => {
    if (players.some((p: Player) => p.role?.id === role?.id)) {
      if (role?.reminders) {
        reminders = [...reminders, ...role.reminders.map(mapReminder(role))];

        if (!session.isPlayerOrSpectator) {
          addSpecialReminders(reminders, role, false);
        }
      }
    }
  });

  reminders.push({
    id: "good",
    role: SPECIAL_REMINDER_ROLES.good,
    name: t('modal.reminder.good'),
  }, {
    id: "evil",
    role: SPECIAL_REMINDER_ROLES.evil,
    name: t('modal.reminder.evil'),
  }, {
    id: "townsfolk",
    role: SPECIAL_REMINDER_ROLES.townsfolk,
    name: t('modal.reminder.townsfolk'),
  }, {
    id: "outsider",
    role: SPECIAL_REMINDER_ROLES.outsider,
    name: t('modal.reminder.outsider'),
  }, {
    id: "minion",
    role: SPECIAL_REMINDER_ROLES.minion,
    name: t('modal.reminder.minion'),
  }, {
    id: "demon",
    role: SPECIAL_REMINDER_ROLES.demon,
    name: t('modal.reminder.demon'),
  }, {
    id: "custom",
    role: SPECIAL_REMINDER_ROLES.custom,
    name: t('modal.reminder.custom'),
  });
  return reminders;
});

async function addReminder(reminder: Reminder) {
  const player = playersStore.players[props.playerIndex];
  if (!player) return;
  let value;
  if (reminder.role?.id === "custom") {
    const name = await dialogStore.prompt(t('prompt.customNote'));
    if (!name) return;
    value = [...player.reminders, { role: SPECIAL_REMINDER_ROLES.custom, name }];
  } else if (reminder.name === "?") {
    let name = await dialogStore.prompt(t('prompt.customNote'));
    if (!name) name = "?";
    value = [...player.reminders, { role: reminder.role, name }];
  } else {
    value = [...player.reminders, reminder];
  }
  playersStore.update({
    player,
    property: "reminders",
    value,
  });
  if (!session.isPlayerOrSpectator && reminder.special) {
    switch (reminder.special.name) {
      case "two-hands":
        playersStore.update({
          player,
          property: "canVoteTwice",
          value: true
        });
        break;
      case "multiplier":
        if (typeof reminder.special.value === "number") {
          playersStore.update({
            player,
            property: "voteValue",
            value: player.voteValue * reminder.special.value
          });
        }
        break;
      case "replace-character":
        playersStore.update({
          player,
          property: "realRole",
          value: reminder.role
        });
        playersStore.update({
          player,
          property: "secretRole",
          value: true
        });
        break;
    }
  }
  grimoire.toggleModal(null);
}
</script>

<style scoped lang="scss">
ul.reminders .reminder {
  background: url("../../assets/reminder.png") center center;
  background-size: 100%;
  width: 14vh;
  height: 14vh;
  max-width: 100px;
  max-height: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 1%;

  &.special {
    background: url("../../assets/reminderSpecial.png") center center;
    background-size: 100%;
  }

  border-radius: 50%;
  border: 3px solid var(--border-color);
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
  cursor: pointer;
  line-height: 100%;
  transition: transform 500ms ease;

  li {
    position: relative;
  }

  picture {
    position: absolute;
    top: 0;
    width: 90%;
    height: 90%;
  }

  picture * {
    max-width: 100%;
    max-height: 100%;
  }

  .text {
    color: black;
    font-size: 65%;
    font-weight: bold;
    text-align: center;
    top: 28%;
    width: 80%;
    line-height: 1;
  }

  &:hover {
    transform: scale(1.2);
  }
}
</style>
